import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { XCircle, Printer, ArrowRight } from "lucide-react";

/**
 * API endpoint builder
 */
const ORDER_DETAILS_ENDPOINT = (orderId) =>
  `https://code.gurusport.in/api/orders/${orderId}`;

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

export default function PaymentFailure() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const txnid = params.get("txnid"); // optional now

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const order = useMemo(() => {
    if (!data) return null;
    const o = data;

    let address = o.user_address || {};
    if (!address || Object.keys(address).length === 0) {
      if (o.user_address_detail) {
        try {
          address =
            typeof o.user_address_detail === "string"
              ? JSON.parse(o.user_address_detail)
              : o.user_address_detail;
        } catch {
          address = {};
        }
      }
    }

    const user = o.user || {};
    const rawItems = Array.isArray(o.order_details?.data)
      ? o.order_details.data
      : [];

    const items = rawItems.map((it) => {
      const prod = it.products || {};
      const qty = it.quantity ?? it.qty ?? 1;
      const price = parseFloat(it.price ?? it.sub_total ?? 0) || 0;
      return {
        id: it.id ?? it.order_id ?? prod.id ?? Math.random(),
        name: prod.name || it.name || "Product",
        sku: prod.sku || it.sku || "-",
        qty,
        price,
        total: +(qty * price).toFixed(2),
        color: it.colors?.name || it.color || "",
        size: it.sizes?.name || it.size || "",
      };
    });

    const subtotal = items.reduce((sum, it) => sum + (it.total || 0), 0);

    const mapped = {
      raw: o,
      id: o.id,
      number: o.order_code || `#${o.id}`,
      createdAt: o.order_date || o.created_at,
      payment: {
        method: o.payment_type || "payu",
        txnid: o.transaction_id || txnid,
        amount: parseFloat(o.total_amount || o.grand_total || subtotal) || 0,
        status: o.payment_status || "failed",
      },
      customer: {
        id: user.id ?? null,
        name: user.name || address.name || "Customer",
        email: user.email || address.email || "",
        phone: address.phone_number || user.phone || "",
      },
      shipping: {
        line1: address.street || "",
        line2: address.apartment_suite || "",
        city:
          address.city?.name ||
          (typeof address.city === "string" ? address.city : ""),
        state:
          address.state?.name ||
          (typeof address.state === "string" ? address.state : ""),
        pincode: address.zipcode || address.postcode || "",
        country:
          address.country?.name ||
          (typeof address.country === "string" ? address.country : "India"),
      },
      items,
      totals: {
        subtotal: subtotal,
        shipping: parseFloat(o.shipping_fee ?? o.shipping_cost ?? 0) || 0,
        tax: parseFloat(o.tax ?? 0) || 0,
        discount: parseFloat(o.discount ?? 0) || 0,
        grand_total:
          parseFloat(o.total_amount ?? o.grand_total ?? subtotal) || 0,
      },
      failureReason:
        o.cancellation_reason ||
        o.failure_reason ||
        (o.payment_status === "unpaid" ? "Payment not completed" : "") ||
        "",
    };

    console.log("🟠 Failure Mapped:", mapped);
    return mapped;
  }, [data, txnid]);

  useEffect(() => {
    if (!orderId) {
      setErr("Missing order_id in URL.");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setErr("");
        const url = ORDER_DETAILS_ENDPOINT(orderId);
        const res = await axios.get(url);

        console.log("📦 API Response (failure):", res.data);
        const payload = res.data?.data ?? res.data;
        console.log("📦 API Response (payload used):", payload);

        setData(payload);
      } catch (e) {
        console.error("❌ API Fetch Error:", e);
        setErr("Could not fetch live order details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderId, txnid]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <p className="text-gray-600">Loading failure receipt…</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-red-600 font-medium">
          Unable to render failure receipt.
        </p>
      </div>
    );
  }

  const {
    number,
    createdAt,
    payment,
    customer,
    shipping,
    items,
    totals,
    failureReason,
  } = order;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 print:p-0">
      {/* Header */}
      <div className="flex items-center justify-center mb-6 gap-2">
        <XCircle className="w-6 h-6 text-red-600" />
        <h1 className="text-xl font-semibold">Payment failed</h1>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-center gap-3 mb-6 no-print">
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50"
        >
          <Printer className="w-4 h-4" /> Print / Save PDF
        </button>

        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-lg bg-black text-white px-4 py-2 text-sm font-medium hover:opacity-90"
        >
          Back to Shop <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Failure card */}
      <div className="bg-white rounded-2xl shadow border print:shadow-none print:border-0">
        {/* Top meta */}
        <div className="p-6 border-b">
          <div className="flex flex-col md:flex-row md:justify-between">
            <div>
              <h2 className="text-lg font-semibold">GURU Sports</h2>
              <p className="text-sm text-gray-600">
                Order payment could not be completed
              </p>
            </div>

            <div className="text-sm text-gray-700 mt-3 md:mt-0">
              <p>
                <span className="text-gray-500 mr-2">Order:</span> {number}
              </p>
              <p>
                <span className="text-gray-500 mr-2">Date:</span>{" "}
                {new Date(createdAt).toLocaleString()}
              </p>
              <p>
                <span className="text-gray-500 mr-2">TxnID:</span>{" "}
                {payment.txnid || "-"}
              </p>
              <p>
                <span className="text-gray-500 mr-2">Method:</span>{" "}
                {payment.method}
              </p>
              <p>
                <span className="text-gray-500 mr-2">Status:</span>{" "}
                <span className="text-red-600">{payment.status}</span>
              </p>
              {failureReason ? (
                <p className="mt-1 text-sm text-red-600">
                  <strong>Reason:</strong> {failureReason}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        {/* Customer + Shipping */}
        <div className="p-6 border-b grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border p-4">
            <h3 className="text-sm font-semibold mb-2">Customer</h3>
            <p className="text-sm font-medium">{customer.name}</p>
            {customer.email ? (
              <p className="text-sm text-gray-600">{customer.email}</p>
            ) : null}
            {customer.phone ? (
              <p className="text-sm text-gray-600">{customer.phone}</p>
            ) : null}
          </div>

          <div className="rounded-lg border p-4">
            <h3 className="text-sm font-semibold mb-2">Shipping Address</h3>
            <p className="text-sm">
              {[shipping.line1, shipping.line2].filter(Boolean).join(", ")}
            </p>
            <p className="text-sm">
              {[shipping.city, shipping.state, shipping.pincode]
                .filter(Boolean)
                .join(", ")}
            </p>
            <p className="text-sm">{shipping.country}</p>
          </div>
        </div>

        {/* Products */}
        <div className="p-6 border-b">
          <h3 className="text-sm font-semibold mb-4">Products</h3>

          {items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2 border">Product</th>
                    <th className="text-left p-2 border">Variant</th>
                    <th className="text-center p-2 border">Qty</th>
                    <th className="text-right p-2 border">Price</th>
                    <th className="text-right p-2 border">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-t">
                      <td className="p-2">{it.name}</td>
                      <td className="p-2">
                        {[it.color, it.size].filter(Boolean).join(" · ") || "-"}
                      </td>
                      <td className="p-2 text-center">{it.qty}</td>
                      <td className="p-2 text-right">{INR.format(it.price)}</td>
                      <td className="p-2 text-right">{INR.format(it.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              No products found for this order.
            </p>
          )}
        </div>

        {/* Totals */}
        <div className="p-6">
          <div className="flex justify-between py-1 text-sm">
            <span>Subtotal</span>
            <span>{INR.format(totals.subtotal)}</span>
          </div>
          <div className="flex justify-between py-1 text-sm">
            <span>Shipping</span>
            <span>{INR.format(totals.shipping)}</span>
          </div>
          <div className="flex justify-between py-1 text-sm">
            <span>Tax</span>
            <span>{INR.format(totals.tax)}</span>
          </div>
          {totals.discount ? (
            <div className="flex justify-between py-1 text-sm">
              <span>Discount</span>
              <span>-{INR.format(totals.discount)}</span>
            </div>
          ) : null}
          <div className="flex justify-between py-2 text-base font-semibold border-t mt-2">
            <span>Grand Total</span>
            <span>{INR.format(totals.grand_total)}</span>
          </div>
        </div>

        {/* Support / notes */}
        <div className="p-6 border-t text-sm text-gray-700">
          <p>
            If you need help, contact our support at{" "}
            <a
              href={`mailto:support@GURU.com?subject=Payment%20failed%20for%20order%20${encodeURIComponent(
                number
              )}`}
              className="text-blue-600 underline"
            >
              support@GURU.com
            </a>
            . You can also retry the payment using the Retry button above.
          </p>
        </div>
      </div>

      {showRaw && (
        <div className="mt-4 bg-gray-50 p-4 rounded-lg text-xs">
          <pre className="whitespace-pre-wrap max-h-64 overflow-auto text-[11px]">
            {JSON.stringify(order.raw ?? data ?? {}, null, 2)}
          </pre>
        </div>
      )}

      {err ? (
        <div className="mt-4 p-3 rounded-lg bg-yellow-50 text-yellow-800 text-sm">
          {err}
        </div>
      ) : null}
    </div>
  );
}
